import * as React from "react";
import { Banner, Button, Field, Input, Modal, ModalBody, Select } from "../components";
import { useAsync, useAction } from "../general/useAsync";
import { useToast } from "../general/Toast";
import * as Configuration from "../configuration/ConfigurationStore";
import * as Store from "./MarkGridStore";

/**
 * Adding structure from the mark screens. Both dialogs go through the mark-grid endpoints rather than the structure
 * editor's, because those also regenerate the module's calculation procedures - so a new assessment counts toward
 * its division mark the moment it exists instead of after someone remembers to regenerate.
 */

/**
 * A new division under the current node, described by its name and type. Calculation coverage is reported
 * through findings.
 */
export const NewDivisionModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    moduleID: string;
    parentGroupID?: string;
    parentName: string;
    onCreated: (created: Store.MarkGridCreatedModel) => void;
}> = ({ isOpen, onClose, moduleID, parentGroupID, parentName, onCreated }) => {
    const toast = useToast();
    const [name, setName] = React.useState("");
    const [typeID, setTypeID] = React.useState("");
    const types = useAsync(() => Store.getGroupTypeCoverage(moduleID), [moduleID, isOpen], { enabled: isOpen, initial: [] });

    React.useEffect(() => {
        if (isOpen) {
            setName("");
            setTypeID("");
        }
    }, [isOpen]);

    const create = useAction(async () => {
        const created = await Store.createDivision({
            name: name.trim(),
            assessmentGroupTypeID: typeID,
            qualificationModuleID: moduleID,
            parentGroupID,
        });
        toast.success(`${created.name} added.`);
        onCreated(created);
        onClose();
    });

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="New division"
            icon={["fad", "folder-plus"]}
            footer={
                <>
                    <Button variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button icon={["fad", "plus"]} onClick={create.run} loading={create.busy} disabled={!name.trim() || !typeID}>
                        Add division
                    </Button>
                </>
            }
        >
            <ModalBody>
                <p className="mb-4 text-sm text-text-weak">
                    Adds a division inside <strong className="text-text">{parentName}</strong>.
                </p>
                <Field label="Name" required>
                    <Input value={name} autoFocus placeholder="e.g. Assessments 2" onChange={(e) => setName(e.target.value)} />
                </Field>
                <Field
                    label="Division type"
                    required
                    hint="Choose the type of division you are adding."
                >
                    <Select value={typeID} placeholder="Select a type" onChange={(e) => setTypeID(e.target.value)}>
                        {(types.data ?? []).map((t) => (
                            <option key={t.assessmentGroupTypeID} value={t.assessmentGroupTypeID}>
                                {t.name}
                            </option>
                        ))}
                    </Select>
                </Field>
                {types.error && <Banner tone="danger">{types.error}</Banner>}
                {create.error && (
                    <Banner tone="danger" className="mt-3">
                        {create.error}
                    </Banner>
                )}
            </ModalBody>
        </Modal>
    );
};

/** A new assessment - a new column - in a division. */
export const NewAssessmentModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    groupID: string;
    groupName: string;
    onCreated: (created: Store.MarkGridCreatedModel) => void;
}> = ({ isOpen, onClose, groupID, groupName, onCreated }) => {
    const toast = useToast();
    const [name, setName] = React.useState("");
    const [typeID, setTypeID] = React.useState("");
    const [total, setTotal] = React.useState("100");
    const types = useAsync(() => Configuration.listAssessmentTypes(), [isOpen], { enabled: isOpen, initial: [] });

    React.useEffect(() => {
        if (isOpen) {
            setName("");
            setTotal("100");
        }
    }, [isOpen]);

    // Most organizations have one or two assessment types; default to the first so the common case is two fields.
    React.useEffect(() => {
        if (!typeID && types.data?.length) setTypeID(types.data[0].id ?? "");
    }, [types.data, typeID]);

    const outOf = Number(total);
    const valid = !!name.trim() && !!typeID && Number.isFinite(outOf) && outOf > 0;

    const create = useAction(async () => {
        const created = await Store.createAssessment({ name: name.trim(), groupID, assessmentTypeID: typeID, total: outOf });
        if (created.counted) toast.success(`${created.name} added.`);
        else toast.warning(`${created.name} added, but no calculation procedure reads it, so it won't count toward the division mark.`);
        onCreated(created);
        onClose();
    });

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="New assessment"
            icon={["fad", "file-circle-plus"]}
            footer={
                <>
                    <Button variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button icon={["fad", "plus"]} onClick={create.run} loading={create.busy} disabled={!valid}>
                        Add assessment
                    </Button>
                </>
            }
        >
            <ModalBody>
                <p className="mb-4 text-sm text-text-weak">
                    Adds a column to <strong className="text-text">{groupName}</strong>.
                </p>
                <Field label="Name" required>
                    <Input value={name} autoFocus placeholder="e.g. Speltoets 4" onChange={(e) => setName(e.target.value)} />
                </Field>
                <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Type" required>
                        <Select value={typeID} placeholder="Select a type" onChange={(e) => setTypeID(e.target.value)}>
                            {(types.data ?? []).map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.name}
                                </option>
                            ))}
                        </Select>
                    </Field>
                    <Field label="Out of" required>
                        <Input type="number" min={1} value={total} onChange={(e) => setTotal(e.target.value)} />
                    </Field>
                </div>
                {create.error && <Banner tone="danger">{create.error}</Banner>}
            </ModalBody>
        </Modal>
    );
};
